import { useMutation, useQueryClient } from 'react-query';
import { useToast } from '@razorpay/blade/components';
import { createFormulaCard } from 'api/gql-utils/overtime/mutations';
import type { CreateFormulaCardInput } from 'api/gql-utils/overtime/types';
import type { OvertimeFormula } from 'components/Settings/AttendanceAndLeavesSettings/types';
import { parseError } from 'utils/Api';
import { AppError } from 'utils/AppError';
import { OVERTIME_FORMULA_CARDS_QUERY_KEY } from './useOvertimeFormulaCards';
import { FORMULA_RATE_TYPE } from 'components/Settings/AttendanceAndLeavesSettings/constants';

const mapFormulaToApiInput = (formula: OvertimeFormula): CreateFormulaCardInput => {
  const rateUnit = formula.unit;

  if (formula.rateType === FORMULA_RATE_TYPE.flat) {
    return {
      name: formula.name,
      rateType: FORMULA_RATE_TYPE.flat,
      rateUnit,
      flatRate: formula.flatAmount ? Number(formula.flatAmount ?? 0) * 100 : undefined,
    };
  }

  const hasDailyAllowance = formula.dailyAllowance;

  return {
    name: formula.name,
    rateType: FORMULA_RATE_TYPE.multiplier,
    rateUnit,
    multiplier: formula.multiplier,
    dailyAllowance: hasDailyAllowance ? Number(formula.dailyAllowance ?? 0) * 100 : undefined,
  };
};

export function useCreateFormulaCard() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (formula: OvertimeFormula) =>
      createFormulaCard({ input: mapFormulaToApiInput(formula) }),
    onSuccess: (_data, formula) => {
      queryClient.invalidateQueries([OVERTIME_FORMULA_CARDS_QUERY_KEY]);
      toast.show({ content: `${formula.name} created`, color: 'positive' });
    },
    onError: (error: typeof AppError) => {
      toast.show({
        content: parseError(error),
        color: 'negative',
      });
    },
  });
}
